import { ReactElement } from "react";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  IconButton,
  Chip,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import DeleteIconButton from "../../../components/DeleteIconButton";
import { TransactionCategory, TransactionCategoryGroup } from "../../../types";

interface SortableCategoryCardProps {
  category: TransactionCategory;
  onEdit: (category: TransactionCategory) => void;
  onDelete: (category: TransactionCategory) => void;
  onDetails: (category: TransactionCategory) => void;
  isCategoryUsed: (categoryName: string) => boolean;
  enableOrdering?: boolean;
  groups: TransactionCategoryGroup[];
}

function SortableCategoryCard({
  category,
  onEdit,
  onDelete,
  onDetails,
  isCategoryUsed,
  enableOrdering = false,
  groups,
}: SortableCategoryCardProps): ReactElement {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const categoryInUse = isCategoryUsed(category.name);

  return (
    <Paper
      ref={setNodeRef}
      style={style}
      elevation={isDragging ? 4 : 0}
      sx={{
        p: 1,
        mb: 0.5,
        border: 1,
        borderColor: "divider",
        borderRadius: 0.5,
        "&:hover": { boxShadow: 1 },
        transition: "box-shadow 0.2s",
        cursor: enableOrdering ? (isDragging ? "grabbing" : "grab") : "default",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        {enableOrdering && (
          <Box
            {...attributes}
            {...listeners}
            sx={{
              display: "flex",
              alignItems: "center",
              cursor: isDragging ? "grabbing" : "grab",
              color: "text.secondary",
              "&:hover": { color: "text.primary" },
              mt: 0.25,
              touchAction: "none", // so touch starts drag, not scroll
            }}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
        )}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" fontWeight="bold" sx={{ lineHeight: 1.3 }}>
            {category.name}
          </Typography>
        </Box>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {(() => {
            const group = category.groupId ? groups.find((g) => g.id === category.groupId) : null;
            const hasDetails = group && group.details && group.details.length > 0;
            const button = (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onDetails(category)}
                disabled={!hasDetails}
                sx={{ minWidth: "auto", height: "32px", px: 1.5 }}
              >
                Select Details
              </Button>
            );
            
            if (!hasDetails) {
              return (
                <Tooltip title="No details available for this category's group. Please add details in the group's 'Manage Details' section first." arrow>
                  <span>{button}</span>
                </Tooltip>
              );
            }
            
            return button;
          })()}
          <Chip
            label={category.enabledDetails && category.enabledDetails.length > 0 ? category.enabledDetails.length : "N/A"}
            size="small"
            sx={{
              height: "24px",
              width: "24px",
              borderRadius: "50%",
              fontSize: "0.65rem",
              "& .MuiChip-label": {
                px: 0,
                py: 0,
              },
            }}
          />
          <IconButton
            size="small"
            onClick={() => onEdit(category)}
            sx={{ color: "primary.main" }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
          <DeleteIconButton
            onClick={() => onDelete(category)}
            disabled={categoryInUse}
            disabledTooltip={`Cannot delete category "${category.name}": It is used in one or more transactions`}
            size="small"
          />
        </Stack>
      </Stack>
    </Paper>
  );
}

export default SortableCategoryCard;
